import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { Check } from "lucide-react";
import GlideMenu from "./GlideMenu";
import type { MenuItem } from "./useMenuItems";

interface NavFlyoutProps {
  items: MenuItem[];
  /** The row it opens from: its top and the x just past it (viewport coordinates). */
  anchor: { top: number; left: number };
  isActive: (item: MenuItem) => boolean;
  onPick: (item: MenuItem) => void;
  /** Hover bookkeeping shared with the rail: entering keeps the panel, leaving starts the close. */
  onEnter: () => void;
  onLeave: () => void;
}

/**
 * A group's sub-items, opened beside the rail on hover (the chat agent's sidebar). Its rows are the rail's
 * own rows — same height, icon and gap — with a check on the current page and the gliding highlight behind
 * them. One level only: a sub-item that holds others (Design Assets) opens its first page; its own page has
 * the rest.
 */
const NavFlyout = ({ items, anchor, isActive, onPick, onEnter, onLeave }: NavFlyoutProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(anchor.top);
  // The first row lines up with the row it opened from; a panel that would run off the screen moves up.
  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 0;
    setTop(Math.max(8, Math.min(anchor.top - 4, window.innerHeight - height - 8)));
  }, [anchor.top, items.length]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      data-testid="nav-flyout"
      className="fixed z-[100] min-w-[220px] rounded-xl border border-black-300 bg-body p-1 font-satoshi shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
      style={{ top, left: anchor.left }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <GlideMenu className="group/glide flex flex-col gap-px" highlightClassName="inset-x-0 rounded-lg bg-trueBlack/[0.06] dark:bg-trueWhite/10">
        {items.map((item) => {
          const active = isActive(item);

          return (
            <button
              key={item.key}
              type="button"
              data-menu-row
              data-testid={`nav-flyout-item-${item.key}`}
              onClick={() => onPick(item)}
              className={clsx(
                "relative z-10 flex h-8 w-full items-center gap-2.5 rounded-lg px-2 text-left text-[14px] text-trueBlack transition-transform active:scale-[0.98] dark:text-white",
                active && "bg-trueBlack/[0.06] group-hover/glide:bg-transparent dark:bg-trueWhite/10"
              )}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">{item.icon && <item.icon className="h-[18px] w-[18px]" />}</span>
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {active && <Check size={16} className="shrink-0" />}
            </button>
          );
        })}
      </GlideMenu>
    </div>,
    document.body
  );
};

export default NavFlyout;
